import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import SidebarItem from "./SidebarItem";
import { SIDEBAR_DRAWER_ID } from "../../hooks/useSidebarDrawer";

/** Breakpoint em que a Sidebar vira drawer modal (espelha o @media do CSS). */
const DRAWER_MEDIA_QUERY = "(max-width: 767px)";

/** Seletor dos elementos focáveis dentro do drawer, para o focus-trap. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Detecta se estamos no viewport de drawer (mobile). Só nesse modo a Sidebar é
 * modal (focus-trap + ESC). Em desktop ela é um landmark fixo e NÃO prende foco.
 */
function isDrawerViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(DRAWER_MEDIA_QUERY).matches;
}

const SidebarNav = styled.nav`
  width: ${(p) => (p.$collapsed ? "64px" : "220px")};
  height: calc(100vh - var(--header-height-app));
  background: var(--nx-surface);
  color: var(--nx-text);
  border-right: 1px solid var(--nx-line);
  transition: width 180ms ease;
  overflow: auto;
  box-shadow: var(--nx-sh-sm);
  flex-shrink: 0;

  /* Mobile: oculto por padrão; drawer overlay quando aberto */
  @media (max-width: 767px) {
    position: fixed;
    left: 0;
    top: var(--header-height-app);
    height: calc(100vh - var(--header-height-app));
    width: 260px;
    z-index: 200;
    box-shadow: var(--nx-sh-md);
    transform: translateX(-100%);
    transition: transform 250ms ease, width 180ms ease;

    ${(p) =>
      p.$drawerOpen &&
      css`
        transform: translateX(0);
      `}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;

  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "space-between")};

  padding: 10px 12px;
  background: var(--nx-primary-50);
  border-bottom: 1px solid var(--nx-line);
`;

const Brand = styled.div`
  display: ${(p) => (p.$collapsed ? "none" : "block")};
  font-size: var(--nx-fs-body);
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--nx-primary-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CollapseButton = styled.button`
  all: unset;
  cursor: pointer;

  width: 32px;
  height: 32px;
  border-radius: var(--nx-r-sm);

  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: var(--nx-primary);
  background: var(--nx-primary-100);
  transition: background 150ms ease;

  &:hover {
    background: var(--nx-primary-100);
    filter: brightness(0.96);
  }

  &:focus-visible {
    outline: 2px solid var(--nx-primary);
    outline-offset: 2px;
  }
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 10px;
  margin: 0;

  a{
  text-decoration: none;
  }
`;

/* Fundo semi-transparente que aparece atrás do drawer no mobile */
const DrawerOverlay = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    position: fixed;
    inset: 0;
    top: var(--header-height-app);
    background: rgba(0, 0, 0, 0.45);
    z-index: 199;
  }

  @media (prefers-reduced-motion: reduce) {
    /* sem fade — basta aparecer/sumir */
  }
`;

function normalizeKeys(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Sidebar — menu lateral da área autenticada.
 * O overlay escuro (DrawerOverlay) é gerenciado internamente pelo componente;
 * páginas que usam <Sidebar> não devem renderizar overlay adicional para evitar duplicidade.
 * As props `drawerOpen` e `onDrawerClose` são usadas apenas no mobile (< 768px);
 * em desktop o componente permanece sempre visível e ignora o estado do drawer.
 */
export default function Sidebar({
  menuItems = [],

  collapsed,
  defaultCollapsed = false,
  onCollapsedChange,

  openKeys,
  defaultOpenKeys = [],
  onOpenKeysChange,

  activeKey,

  /** NOVO (opcional): texto/logo no topo */
  headerTitle = "Menu",
  /** NOVO (opcional): esconder header inteiro */
  showHeader = true,

  /**
   * Mobile drawer: quando true, abre o drawer sobre o conteúdo.
   * Ignorado em viewports ≥ 768px (desktop sempre visível).
   */
  drawerOpen = false,
  /** Callback chamado quando o usuário fecha o drawer (clique no overlay ou item) */
  onDrawerClose,
}) {
  const isCollapsedControlled = typeof collapsed === "boolean";
  const isOpenKeysControlled = Array.isArray(openKeys);

  // Ref do container do drawer (para focus-trap) e do elemento que tinha o foco
  // ANTES de abrir (o hamburguer), para devolvê-lo ao fechar.
  const drawerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const getFocusables = useCallback(() => {
    const el = drawerRef.current;
    if (!el) return [];
    return Array.from(el.querySelectorAll(FOCUSABLE_SELECTOR));
  }, []);

  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [internalOpenKeys, setInternalOpenKeys] = useState(
    normalizeKeys(defaultOpenKeys)
  );

  const resolvedCollapsed = isCollapsedControlled ? collapsed : internalCollapsed;

  const resolvedOpenKeys = useMemo(
    () => (isOpenKeysControlled ? openKeys : internalOpenKeys),
    [isOpenKeysControlled, openKeys, internalOpenKeys]
  );

  useEffect(() => {
    if (isCollapsedControlled) setInternalCollapsed(collapsed);
  }, [isCollapsedControlled, collapsed]);

  useEffect(() => {
    if (isOpenKeysControlled) setInternalOpenKeys(openKeys);
  }, [isOpenKeysControlled, openKeys]);

  const setCollapsedState = (next) => {
    if (onCollapsedChange) onCollapsedChange(next);
    if (!isCollapsedControlled) setInternalCollapsed(next);
  };

  const setOpenKeysState = (next) => {
    if (onOpenKeysChange) onOpenKeysChange(next);
    if (!isOpenKeysControlled) setInternalOpenKeys(next);
  };

  const toggleOpen = (key) => {
    const next = resolvedOpenKeys.includes(key)
      ? resolvedOpenKeys.filter((k) => k !== key)
      : [...resolvedOpenKeys, key];

    setOpenKeysState(next);
  };

  const safeMenuItems = Array.isArray(menuItems) ? menuItems : [];

  const handleToggleCollapsed = () => {
    setCollapsedState(!resolvedCollapsed);
  };

  // Acessibilidade de teclado do drawer mobile: ESC fecha, Tab/Shift+Tab ficam
  // presos dentro do drawer, o foco entra ao abrir e volta ao hamburguer ao
  // fechar. Só vale no MODO DRAWER (mobile aberto) — em desktop o `return` a
  // seguir aborta o efeito antes de prender qualquer foco.
  useEffect(() => {
    if (!drawerOpen || !isDrawerViewport()) return undefined;

    const drawerEl = drawerRef.current;
    if (!drawerEl) return undefined;

    // Guarda quem tinha o foco (o hamburguer) para restaurar no cleanup.
    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Move o foco para dentro do drawer ao abrir.
    const focusables = getFocusables();
    if (focusables.length > 0) {
      focusables[0].focus();
    } else {
      drawerEl.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (onDrawerClose) onDrawerClose();
        return;
      }

      if (event.key !== "Tab") return;

      const items = getFocusables();
      if (items.length === 0) {
        // Sem focáveis: mantém o foco no container.
        event.preventDefault();
        drawerEl.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        // Shift+Tab no primeiro (ou foco fora do drawer) → vai para o último.
        if (active === first || !drawerEl.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !drawerEl.contains(active)) {
        // Tab no último (ou foco fora do drawer) → volta para o primeiro.
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Devolve o foco ao trigger (hamburguer) ao fechar por ESC/overlay/navegação.
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
      previouslyFocusedRef.current = null;
    };
  }, [drawerOpen, onDrawerClose, getFocusables]);

  return (
    <>
      {/* Overlay escuro — só aparece no mobile quando o drawer está aberto */}
      {drawerOpen && (
        <DrawerOverlay
          aria-hidden="true"
          onClick={onDrawerClose}
        />
      )}

      <SidebarNav
        ref={drawerRef}
        id={SIDEBAR_DRAWER_ID}
        /* Modo drawer (mobile aberto): vira um diálogo modal — o focus-trap/ESC
           deste componente reforçam a modalidade e o aria-modal a anuncia ao
           leitor de tela. Em desktop permanece um landmark de navegação. */
        role={drawerOpen ? "dialog" : "navigation"}
        aria-label="Menu lateral"
        aria-modal={drawerOpen ? "true" : undefined}
        /* Focável por programa (não por Tab) para receber o foco ao abrir
           quando não há elementos focáveis internos. */
        tabIndex={drawerOpen ? -1 : undefined}
        $collapsed={resolvedCollapsed}
        $drawerOpen={drawerOpen}
      >
        {showHeader && (
          <SidebarHeader $collapsed={resolvedCollapsed}>
            <Brand $collapsed={resolvedCollapsed} title={headerTitle}>
              {headerTitle}
            </Brand>

            <CollapseButton
              type="button"
              onClick={handleToggleCollapsed}
              aria-label={resolvedCollapsed ? "Expandir menu" : "Recolher menu"}
              title={resolvedCollapsed ? "Expandir" : "Recolher"}
            >
              {/* Ícone simples (troque por react-icons se quiser) */}
              {resolvedCollapsed ? "»" : "«"}
            </CollapseButton>
          </SidebarHeader>
        )}

        <MenuList role="list">
          {safeMenuItems.map((item) => (
            <SidebarItem
              key={item.key}
              item={item}
              collapsed={resolvedCollapsed}
              openKeys={resolvedOpenKeys}
              onToggleOpen={toggleOpen}
              activeKey={activeKey}
              level={0}
              onItemClick={onDrawerClose}
            />
          ))}
        </MenuList>
      </SidebarNav>
    </>
  );
}

Sidebar.propTypes = {
  menuItems: PropTypes.array.isRequired,

  collapsed: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,

  openKeys: PropTypes.arrayOf(PropTypes.string),
  defaultOpenKeys: PropTypes.arrayOf(PropTypes.string),
  onOpenKeysChange: PropTypes.func,

  activeKey: PropTypes.string,

  headerTitle: PropTypes.string,
  showHeader: PropTypes.bool,

  drawerOpen: PropTypes.bool,
  onDrawerClose: PropTypes.func,
};